/*
 * SundayBuilder · Planner-First Service Board
 * ---------------------------------------------------------------------
 * Blank service order rendered from the resolved denomination-aware
 * template. The planner enters music manually or invokes Liturgical
 * Intelligence per slot. No LLM fabrication — LI suggestions come
 * only from the parish's verified primary/secondary hymnals and the
 * governed choral/organ catalogs.
 * =================================================================== */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Sparkles, Plus, X, ArrowUp, ArrowDown, Loader2, Info, Check,
  ChevronRight,
} from "lucide-react";
import "./planner-first.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MUSIC_CATEGORIES = new Set([
  "prelude", "postlude", "congregational_hymn",
  "choral_anthem", "ordinary", "psalm", "meditation",
]);

const asMusicSlot = (s) => MUSIC_CATEGORIES.has(s.category) || s.custom;

function uniqInstanceId(base, existing) {
  let i = 1;
  let id = `${base}#${i}`;
  const set = new Set(existing.map((x) => x.instance_id));
  while (set.has(id)) { i += 1; id = `${base}#${i}`; }
  return id;
}

/**
 * Build the initial planner state from the resolved template.
 * If a persisted `initialState` is present, we hydrate from it and
 * never clobber the user's stored music.
 */
function initFromTemplate(template, initialState) {
  if (initialState && Array.isArray(initialState.slots) && initialState.slots.length > 0) {
    return {
      tradition: initialState.tradition || template?.tradition || "",
      slots: initialState.slots.map((s) => ({
        instance_id: s.instance_id || s.slot_id,
        slot_id:     s.slot_id,
        label:       s.label,
        category:    s.category,
        optional:    !!s.optional,
        multiple:    !!s.multiple,
        position:    Number(s.position || 0),
        custom:      !!s.custom,
        selections:  Array.isArray(s.selections) ? s.selections : [],
        notes:       s.notes || "",
      })),
    };
  }
  return {
    tradition: template?.tradition || "",
    slots: (template?.slots || []).map((s) => ({
      instance_id: s.slot_id,
      slot_id:     s.slot_id,
      label:       s.label,
      category:    s.category,
      optional:    !!s.optional,
      multiple:    !!s.multiple,
      position:    Number(s.position || 0),
      custom:      !!s.custom,
      selections:  [],
      notes:       "",
    })),
  };
}

/**
 * Props:
 *   jobId        · when set, we persist to /parish/job/{jobId}/planner-state
 *   sundayDate   · ISO date being planned (used only for LI proper_id hint)
 *   sundaySeason · optional season string
 *   properId     · optional lectionary proper_id
 *   initialState · persisted planner state (report.planner_state) if any
 */
export default function PlannerFirstBoard({
  jobId, sundayDate, sundaySeason, properId, rclTrack, initialState,
}) {
  const [template, setTemplate]   = useState(null);
  const [error, setError]         = useState(null);
  const [state, setState]         = useState(null);
  const [savingAt, setSavingAt]   = useState(null);   // "saving" | "saved" | null
  const saveTimerRef              = useRef(null);
  const initialLoadRef            = useRef(true);

  // ---- Load template ----
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sundaybuilder/service-template`)
      .then((r) => {
        if (cancelled) return;
        setTemplate(r.data);
        setState(initFromTemplate(r.data, initialState));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail
          || "The service template is temporarily unavailable.");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Debounced auto-save ----
  useEffect(() => {
    if (!jobId || !state) return;
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    setSavingAt("saving");
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/parish/job/${jobId}/planner-state`, {
          tradition: state.tradition || "",
          rcl_track: rclTrack || null,
          slots:     state.slots,
        });
        setSavingAt("saved");
        setTimeout(() => setSavingAt(null), 1500);
      } catch (_e) {
        setSavingAt(null);
      }
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [state, jobId, rclTrack]);

  const sortedSlots = useMemo(
    () => (state?.slots || []).slice().sort((a, b) => a.position - b.position),
    [state]
  );

  /* ---- Mutators ---- */
  const patchSlot = useCallback((instance_id, patch) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => s.instance_id === instance_id ? { ...s, ...patch } : s),
    }));
  }, []);

  const removeSlot = useCallback((instance_id) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.filter((s) => s.instance_id !== instance_id),
    }));
  }, []);

  const reorderSlot = useCallback((instance_id, direction) => {
    setState((prev) => {
      const list = prev.slots.slice().sort((a, b) => a.position - b.position);
      const idx = list.findIndex((s) => s.instance_id === instance_id);
      if (idx < 0) return prev;
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= list.length) return prev;
      const a = list[idx], b = list[swap];
      const pa = a.position, pb = b.position;
      return {
        ...prev,
        slots: prev.slots.map((s) => {
          if (s.instance_id === a.instance_id) return { ...s, position: pb };
          if (s.instance_id === b.instance_id) return { ...s, position: pa };
          return s;
        }),
      };
    });
  }, []);

  const addCustomSlot = useCallback(() => {
    setState((prev) => {
      const maxPos = prev.slots.reduce((m, s) => Math.max(m, s.position || 0), 0);
      const instance_id = uniqInstanceId("custom", prev.slots);
      return {
        ...prev,
        slots: [
          ...prev.slots,
          {
            instance_id,
            slot_id:  "custom",
            label:    "Custom slot",
            category: "custom",
            optional: true,
            multiple: false,
            position: maxPos + 1,
            custom:   true,
            selections: [],
            notes: "",
          },
        ],
      };
    });
  }, []);

  const addSelection = useCallback((instance_id, initial = {}) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.instance_id !== instance_id) return s;
        return {
          ...s,
          selections: [...s.selections, {
            title: "", tune: "", composer: "", hymnal: "", hymn_number: "",
            source: "manual", available_in: null,
            added_at: new Date().toISOString(),
            ...initial,
          }],
        };
      }),
    }));
  }, []);

  const patchSelection = useCallback((instance_id, idx, patch) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.instance_id !== instance_id) return s;
        return {
          ...s,
          selections: s.selections.map((sel, i) => i === idx ? { ...sel, ...patch } : sel),
        };
      }),
    }));
  }, []);

  const removeSelection = useCallback((instance_id, idx) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.instance_id !== instance_id) return s;
        return { ...s, selections: s.selections.filter((_, i) => i !== idx) };
      }),
    }));
  }, []);

  /* ---- Rendering ---- */
  if (error) {
    return (
      <div className="pfb-error" data-testid="pfb-error">
        <p>{error}</p>
      </div>
    );
  }
  if (!state || !template) {
    return (
      <div className="pfb-loading" data-testid="pfb-loading">
        <Loader2 className="pfb-spin" size={20} /> Preparing your service order…
      </div>
    );
  }

  return (
    <section
      className="pfb-shell"
      data-testid="planner-first-board"
      data-tradition={template.tradition}
    >
      <header className="pfb-header">
        <div>
          <p className="pfb-eyebrow">Service Order · {template.tradition}</p>
          <h2 className="pfb-title" data-testid="pfb-title">
            Build your service, one selection at a time.
          </h2>
          <p className="pfb-sub">
            Every field starts empty. Enter your own music, or ask Liturgical
            Intelligence for guidance drawn only from your parish's configured
            resources.
          </p>
        </div>
        {jobId && (
          <div className="pfb-savestate" data-testid="pfb-savestate">
            {savingAt === "saving" && <><Loader2 className="pfb-spin" size={14} /> Saving…</>}
            {savingAt === "saved"  && <><Check size={14} /> Saved</>}
          </div>
        )}
      </header>

      <ol className="pfb-list" data-testid="pfb-slot-list">
        {sortedSlots.map((slot, idx) => (
          <SlotCard
            key={slot.instance_id}
            slot={slot}
            index={idx}
            first={idx === 0}
            last={idx === sortedSlots.length - 1}
            sundayDate={sundayDate}
            sundaySeason={sundaySeason}
            properId={properId}
            rclTrack={rclTrack}
            onPatchSlot={patchSlot}
            onRemoveSlot={removeSlot}
            onReorderSlot={reorderSlot}
            onAddSelection={addSelection}
            onPatchSelection={patchSelection}
            onRemoveSelection={removeSelection}
          />
        ))}
      </ol>

      <div className="pfb-footer">
        <button
          className="pfb-btn ghost"
          onClick={addCustomSlot}
          data-testid="pfb-add-custom-slot"
        >
          <Plus size={16} /> Add Custom Slot
        </button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Per-slot card                                                      */
/* ------------------------------------------------------------------ */
function SlotCard({
  slot, index, first, last,
  sundayDate, sundaySeason, properId, rclTrack,
  onPatchSlot, onRemoveSlot, onReorderSlot,
  onAddSelection, onPatchSelection, onRemoveSelection,
}) {
  const [liOpen, setLiOpen]         = useState(false);
  const [liLoading, setLiLoading]   = useState(false);
  const [liError, setLiError]       = useState(null);
  const [liMessage, setLiMessage]   = useState(null);
  const [liResults, setLiResults]   = useState([]);
  const [addedIds, setAddedIds]     = useState(new Set());

  const showLIButton = slot.category !== "custom"
    && slot.category !== "reader_element"
    && MUSIC_CATEGORIES.has(slot.category);

  const openLI = async () => {
    if (liOpen) { setLiOpen(false); return; }
    setLiOpen(true);
    setLiLoading(true);
    setLiError(null); setLiMessage(null); setLiResults([]); setAddedIds(new Set());
    try {
      const r = await axios.post(`${API}/sundaybuilder/suggest`, {
        slot_id:       slot.slot_id,
        proper_id:     properId || null,
        sunday_season: sundaySeason || null,
        rcl_track:     rclTrack || null,
        limit:         10,
      });
      setLiResults(r.data.recommendations || []);
      setLiMessage(r.data.message || null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setLiError(typeof detail === "string" ? detail
        : "Liturgical Intelligence could not respond. Please try again.");
    } finally {
      setLiLoading(false);
    }
  };

  const acceptSuggestion = (rec) => {
    onAddSelection(slot.instance_id, {
      title:            rec.title || "",
      tune:             rec.tune || "",
      composer:         rec.composer || "",
      arranger:         rec.arranger || "",
      hymnal:           rec.hymnal || rec.hymnal_source_id || "",
      hymnal_source_id: rec.hymnal_source_id || "",
      hymn_number:      rec.hymn_number || rec.number || "",
      canonical_hymn_id: rec.canonical_hymn_id || rec.work_id || "",
      available_in:     rec.available_in || null,
      reason:           rec.reason || "",
      themes_matched:   rec.themes_matched || [],
      source:           "liturgical_intelligence",
    });
    const key = rec.canonical_hymn_id || rec.work_id || `${rec.title}|${rec.hymn_number || ""}`;
    setAddedIds((prev) => new Set([...prev, key]));
  };

  // For single-selection slots, coerce to one row visible even when empty.
  const rows = slot.selections.length === 0
    ? [null]
    : slot.selections;

  const canAddAnother = slot.multiple;
  const canRemove     = slot.optional && !slot.custom;
  const isCustom      = slot.custom;

  return (
    <li
      className={`pfb-slot ${slot.category}`}
      data-testid={`pfb-slot-${slot.slot_id}`}
      data-instance-id={slot.instance_id}
    >
      <div className="pfb-slot-head">
        <div className="pfb-slot-meta">
          <span className="pfb-slot-pos" aria-hidden="true">{index + 1}</span>
          {isCustom ? (
            <input
              type="text"
              className="pfb-label-input"
              value={slot.label}
              onChange={(e) => onPatchSlot(slot.instance_id, { label: e.target.value })}
              data-testid={`pfb-slot-label-${slot.instance_id}`}
              placeholder="Slot name (e.g. Blessing of Palms)"
            />
          ) : (
            <span className="pfb-slot-label" data-testid={`pfb-slot-label-${slot.instance_id}`}>
              {slot.label}
            </span>
          )}
          {slot.optional && (
            <span className="pfb-badge subtle" title="This slot is optional for your denomination">optional</span>
          )}
          {slot.multiple && (
            <span className="pfb-badge subtle" title="You can add more than one selection here">repeatable</span>
          )}
          {isCustom && (
            <span className="pfb-badge subtle">custom</span>
          )}
        </div>
        <div className="pfb-slot-controls">
          {(isCustom || slot.optional) && (
            <>
              <button
                className="pfb-icon-btn"
                aria-label="Move up"
                disabled={first}
                onClick={() => onReorderSlot(slot.instance_id, "up")}
                data-testid={`pfb-slot-up-${slot.instance_id}`}
              >
                <ArrowUp size={14} />
              </button>
              <button
                className="pfb-icon-btn"
                aria-label="Move down"
                disabled={last}
                onClick={() => onReorderSlot(slot.instance_id, "down")}
                data-testid={`pfb-slot-down-${slot.instance_id}`}
              >
                <ArrowDown size={14} />
              </button>
            </>
          )}
          {(canRemove || isCustom) && (
            <button
              className="pfb-icon-btn danger"
              aria-label="Remove slot"
              onClick={() => onRemoveSlot(slot.instance_id)}
              data-testid={`pfb-slot-remove-${slot.instance_id}`}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="pfb-slot-body">
        {rows.map((sel, i) => (
          <SelectionRow
            key={i}
            slot={slot}
            selection={sel}
            index={i}
            onPatch={(patch) => {
              if (sel === null) {
                onAddSelection(slot.instance_id, patch);
              } else {
                onPatchSelection(slot.instance_id, i, patch);
              }
            }}
            onRemove={sel ? () => onRemoveSelection(slot.instance_id, i) : null}
          />
        ))}

        <div className="pfb-slot-actions">
          {showLIButton && (
            <button
              className={`pfb-btn ${liOpen ? "primary" : "ghost accent"}`}
              onClick={openLI}
              data-testid={`pfb-ask-li-${slot.instance_id}`}
            >
              <Sparkles size={14} />
              {liOpen ? "Hide Liturgical Intelligence" : "Ask Liturgical Intelligence"}
            </button>
          )}
          {canAddAnother && (
            <button
              className="pfb-btn ghost"
              onClick={() => onAddSelection(slot.instance_id)}
              data-testid={`pfb-add-another-${slot.instance_id}`}
            >
              <Plus size={14} /> Add another selection
            </button>
          )}
        </div>

        <NotesField slot={slot} onChange={(notes) => onPatchSlot(slot.instance_id, { notes })} />

        {liOpen && (
          <LIPanel
            slot={slot}
            loading={liLoading}
            error={liError}
            message={liMessage}
            results={liResults}
            addedIds={addedIds}
            onAccept={acceptSuggestion}
            onClose={() => setLiOpen(false)}
          />
        )}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Selection row (manual entry)                                       */
/* ------------------------------------------------------------------ */
function SelectionRow({ slot, selection, index, onPatch, onRemove }) {
  const isFromLI = selection?.source === "liturgical_intelligence";
  const s = selection || { title: "", tune: "", composer: "", hymnal: "", hymn_number: "" };

  return (
    <div
      className={`pfb-row ${isFromLI ? "from-li" : ""}`}
      data-testid={`pfb-selection-${slot.instance_id}-${index}`}
    >
      <div className="pfb-row-fields">
        <input
          type="text"
          className="pfb-input title"
          placeholder={slot.category === "choral_anthem" ? "Anthem title" : "Title"}
          value={s.title || ""}
          onChange={(e) => onPatch({ title: e.target.value, source: isFromLI ? s.source : "manual" })}
          data-testid={`pfb-input-title-${slot.instance_id}-${index}`}
        />
        <input
          type="text"
          className="pfb-input meta"
          placeholder={slot.category === "choral_anthem" ? "Composer / arranger" : "Tune"}
          value={slot.category === "choral_anthem" ? (s.composer || "") : (s.tune || "")}
          onChange={(e) => onPatch(slot.category === "choral_anthem"
            ? { composer: e.target.value }
            : { tune: e.target.value })}
          data-testid={`pfb-input-meta-${slot.instance_id}-${index}`}
        />
        <input
          type="text"
          className="pfb-input hymnal"
          placeholder="Hymnal / resource"
          value={s.hymnal || ""}
          onChange={(e) => onPatch({ hymnal: e.target.value })}
          data-testid={`pfb-input-hymnal-${slot.instance_id}-${index}`}
        />
        <input
          type="text"
          className="pfb-input number"
          placeholder="No."
          value={s.hymn_number || ""}
          onChange={(e) => onPatch({ hymn_number: e.target.value })}
          data-testid={`pfb-input-number-${slot.instance_id}-${index}`}
        />
        {onRemove && (
          <button
            className="pfb-icon-btn danger"
            aria-label="Remove selection"
            onClick={onRemove}
            data-testid={`pfb-remove-selection-${slot.instance_id}-${index}`}
          >
            <X size={14} />
          </button>
        )}
      </div>
      {isFromLI && (
        <div className="pfb-row-metaline">
          <ResourceBadge tier={s.available_in} />
          {s.reason && <span className="pfb-reason"><Info size={12} /> {s.reason}</span>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Notes                                                              */
/* ------------------------------------------------------------------ */
function NotesField({ slot, onChange }) {
  const [open, setOpen] = useState(!!slot.notes);
  if (!open) {
    return (
      <button
        className="pfb-notes-toggle"
        onClick={() => setOpen(true)}
        data-testid={`pfb-notes-open-${slot.instance_id}`}
      >
        + Add planner notes
      </button>
    );
  }
  return (
    <textarea
      className="pfb-notes"
      placeholder="Planner notes (visible only to your team)"
      value={slot.notes}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      data-testid={`pfb-notes-${slot.instance_id}`}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Liturgical Intelligence in-context panel                           */
/* ------------------------------------------------------------------ */
function LIPanel({ slot, loading, error, message, results, addedIds, onAccept, onClose }) {
  return (
    <div
      className="pfb-li-panel"
      role="region"
      aria-label={`Liturgical Intelligence · ${slot.label}`}
      data-testid={`pfb-li-panel-${slot.instance_id}`}
    >
      <div className="pfb-li-head">
        <div>
          <span className="pfb-li-eyebrow"><Sparkles size={12} /> Liturgical Intelligence</span>
          <p className="pfb-li-title">Suggestions for <b>{slot.label}</b></p>
        </div>
        <button
          className="pfb-icon-btn"
          onClick={onClose}
          aria-label="Close suggestions"
          data-testid={`pfb-li-close-${slot.instance_id}`}
        >
          <X size={14} />
        </button>
      </div>

      {loading && (
        <div className="pfb-li-loading" data-testid={`pfb-li-loading-${slot.instance_id}`}>
          <Loader2 className="pfb-spin" size={16} /> Consulting your parish's configured resources…
        </div>
      )}

      {!loading && error && (
        <p className="pfb-li-error" data-testid={`pfb-li-error-${slot.instance_id}`}>{error}</p>
      )}

      {!loading && !error && message && results.length === 0 && (
        <p className="pfb-li-empty" data-testid={`pfb-li-empty-${slot.instance_id}`}>{message}</p>
      )}

      {!loading && !error && results.length > 0 && (
        <ul className="pfb-li-list">
          {results.map((rec, i) => {
            const key = rec.canonical_hymn_id || rec.work_id || `${rec.title}|${rec.hymn_number || ""}|${i}`;
            const added = addedIds.has(key);
            return (
              <li
                key={key}
                className="pfb-li-item"
                data-testid={`pfb-li-item-${slot.instance_id}-${i}`}
              >
                <div className="pfb-li-item-body">
                  <div className="pfb-li-item-top">
                    <span className="pfb-li-item-title">{rec.title || "(untitled)"}</span>
                    <ResourceBadge tier={rec.available_in} />
                  </div>
                  <div className="pfb-li-item-meta">
                    {rec.composer && <span>{rec.composer}</span>}
                    {rec.tune && <span>Tune · {rec.tune}</span>}
                    {(rec.hymnal || rec.hymnal_source_id) && (
                      <span>{rec.hymnal || rec.hymnal_source_id}</span>
                    )}
                    {(rec.hymn_number || rec.number) && (
                      <span>No. {rec.hymn_number || rec.number}</span>
                    )}
                  </div>
                  {rec.reason && (
                    <p className="pfb-li-item-reason">Why this fits: {rec.reason}</p>
                  )}
                </div>
                <button
                  className={`pfb-btn ${added ? "ghost" : "primary"} pfb-li-add`}
                  onClick={() => onAccept(rec)}
                  disabled={added}
                  data-testid={`pfb-li-add-${slot.instance_id}-${i}`}
                >
                  {added ? <><Check size={14} /> Added</> : <><ChevronRight size={14} /> Add to Plan</>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Resource badge                                                     */
/* ------------------------------------------------------------------ */
function ResourceBadge({ tier }) {
  if (!tier) return null;
  const label =
    tier === "primary"          ? "Primary Resource" :
    tier === "secondary"        ? "Secondary Resource" :
    tier === "choral_catalog"   ? "Governed Choral Catalog" :
    tier === "manual"           ? "Manual Entry" :
    tier;
  const cls =
    tier === "primary"          ? "primary" :
    tier === "secondary"        ? "secondary" :
    tier === "choral_catalog"   ? "choral" :
    "neutral";
  return (
    <span className={`pfb-badge ${cls}`} data-testid={`pfb-resource-badge-${tier}`}>
      {label}
    </span>
  );
}
